import { Input, Spinner, Text } from "@ttc3k/trekker";
import { Wrench } from "iconoir-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { WhereaboutsModal } from "shared/components";
import { formatRenewalDate } from "shared/utils";
import { Flex } from "styled-system/jsx";
import { ChoiceOption } from "./ChoiceOption";
import { GracePeriodStep } from "./GracePeriodStep";
import {
  emptyGraceChoice,
  graceBlocksContinue,
  graceVariables,
  type GraceChoice,
} from "./graceChoice";
import type {
  ChecklistFixAction,
  ChecklistFixPreview,
  SetDatesOptions,
} from "./useMembershipChecklist";

const parseDateInput = (value: string): Date | undefined => {
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
};

type ChecklistFixDialogProps = {
  action: ChecklistFixAction;
  needsDate: boolean;
  /**
   * Fixed-date SET_DATES: the end of the current cycle. When set, the dialog
   * asks whether members already paid for it.
   */
  cycleEndDate?: string | null;
  onPreview: (
    date?: Date,
    setDates?: SetDatesOptions,
  ) => Promise<ChecklistFixPreview>;
  onApply: (date?: Date, setDates?: SetDatesOptions) => Promise<void>;
  onClose: () => void;
  membershipTranslationValues: Record<string, string>;
};

/** Previews a checklist fix (dry run) before applying it. */
export const ChecklistFixDialog = ({
  action,
  needsDate,
  cycleEndDate,
  onPreview,
  onApply,
  onClose,
  membershipTranslationValues,
}: ChecklistFixDialogProps) => {
  const { t } = useTranslation("home", {
    keyPrefix: "VIEWS.SETTINGS.VIEWS.MEMBERSHIP_TIERS.CHECKLIST.FIX",
  });
  const { t: tCore } = useTranslation("core");
  const [dateInput, setDateInput] = useState("");
  const [cyclePaid, setCyclePaid] = useState(true);
  const [graceChoice, setGraceChoice] = useState<GraceChoice>(emptyGraceChoice);
  const [preview, setPreview] = useState<ChecklistFixPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [applying, setApplying] = useState(false);

  const date = needsDate ? parseDateInput(dateInput) : undefined;
  const waitingForDate = needsDate && !date;
  const setDates: SetDatesOptions | undefined =
    action === "SET_DATES"
      ? {
          ...(cycleEndDate ? { cyclePaid } : {}),
          ...graceVariables(graceChoice),
        }
      : undefined;

  useEffect(() => {
    if (waitingForDate) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewing(true);
    setPreviewError(false);
    onPreview(date, setDates)
      .then(result => !cancelled && setPreview(result))
      .catch(() => !cancelled && setPreviewError(true))
      .finally(() => !cancelled && setPreviewing(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-preview only when the inputs change
  }, [
    dateInput,
    waitingForDate,
    cyclePaid,
    graceChoice.graceDays,
    graceChoice.neverExpire,
  ]);

  const tv = { ...membershipTranslationValues, count: preview?.affected ?? 0 };
  const grace = preview?.grace;
  const showGrace =
    action === "SET_DATES" && !!grace && grace.overdueCount > 0;

  const renderCycleQuestion = () => {
    if (action !== "SET_DATES" || !cycleEndDate) return null;
    const cycleEnd = formatRenewalDate(cycleEndDate, "MMMM d, yyyy");
    const previewDate = preview?.date
      ? formatRenewalDate(preview.date, "MMMM d, yyyy")
      : "";
    return (
      <Flex flexDir="column" gap="100" role="radiogroup">
        <Text visual="smallSemiBold" color="text.dark">
          {t("SET_DATES.CYCLE_QUESTION", { ...tv, date: cycleEnd })}
        </Text>
        <ChoiceOption
          label={t("SET_DATES.CYCLE_PAID")}
          hint={
            cyclePaid && previewDate
              ? t("SET_DATES.CYCLE_PAID_HINT", { date: previewDate })
              : undefined
          }
          selected={cyclePaid}
          disabled={applying}
          onSelect={() => {
            setCyclePaid(true);
            setGraceChoice(emptyGraceChoice);
          }}
        />
        <ChoiceOption
          label={t("SET_DATES.CYCLE_OWED")}
          hint={
            !cyclePaid && previewDate
              ? t("SET_DATES.CYCLE_OWED_HINT", { date: previewDate })
              : undefined
          }
          selected={!cyclePaid}
          disabled={applying}
          onSelect={() => {
            setCyclePaid(false);
            setGraceChoice(emptyGraceChoice);
          }}
        />
      </Flex>
    );
  };

  const renderPreview = () => {
    if (waitingForDate) {
      return (
        <Text size="sm" color="text.mid">
          {t("PICK_DATE")}
        </Text>
      );
    }
    if (previewing && !preview) return <Spinner w="16px" h="16px" />;
    if (previewError || !preview) {
      return (
        <Text size="sm" color="text.error.mid">
          {t("PREVIEW_ERROR")}
        </Text>
      );
    }
    if (preview.affected === 0) {
      return (
        <Text size="sm" color="text.mid">
          {t("NOTHING_TO_DO")}
        </Text>
      );
    }
    return (
      <Flex flexDir="column" gap="100">
        <Text size="sm" color="text.dark">
          {t(`${action}.PREVIEW`, {
            ...tv,
            date: preview.date
              ? formatRenewalDate(preview.date, "MMMM d, yyyy")
              : "",
          })}
        </Text>
        {action === "SET_DATES" &&
          !showGrace &&
          (preview.phaseAfter === "RENEWAL_WINDOW" ||
            preview.phaseAfter === "EXPIRED") && (
            <Text size="sm" color="text.warning.mid">
              {t(`SET_DATES.PHASE_${preview.phaseAfter}`, tv)}
            </Text>
          )}
        <Text size="sm" color="text.light">
          {t("QUIET_NOTE")}
        </Text>
      </Flex>
    );
  };

  return (
    <WhereaboutsModal
      isOpen
      onClose={onClose}
      title={t(`${action}.TITLE`, membershipTranslationValues)}
      description={t(`${action}.DESCRIPTION`, membershipTranslationValues)}
      HeaderIcon={Wrench}
      size="md"
      saveButtonCopy={t("CONFIRM")}
      cancelButtonCopy={tCore("CANCEL")}
      saveButtonProps={{
        visual: "accent.blue",
        disabled:
          applying ||
          previewing ||
          !preview ||
          preview.affected === 0 ||
          (showGrace && graceBlocksContinue(grace, graceChoice)),
        isLoading: applying,
          onClick: async () => {
          setApplying(true);
          try {
            await onApply(
              date,
              setDates && !showGrace
                ? cycleEndDate
                  ? { cyclePaid }
                  : {}
                : setDates,
            );
          } finally {
            setApplying(false);
          }
        },
      }}
      cancelButtonProps={{ onClick: onClose }}
      isMaxZIndex
    >
      <Flex flexDir="column" gap="300">
        {needsDate && (
          <Input
            label={t("SET_DATES.DATE_LABEL")}
            type="date"
            value={dateInput}
            disabled={applying}
            onChange={e => setDateInput(e.target.value)}
          />
        )}
        {renderCycleQuestion()}
        {renderPreview()}
        {showGrace && grace && (
          <GracePeriodStep
            grace={grace}
            choice={graceChoice}
            onChange={setGraceChoice}
            disabled={applying}
            membershipTranslationValues={membershipTranslationValues}
          />
        )}
      </Flex>
    </WhereaboutsModal>
  );
};
